import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getProductSizePrice, updateProductSizePrice, productImagesAPI, sizeOptionsAPI } from '../../services/api';
import ImageUpload from './ImageUpload';
import './EditSizeDialog.css';

const EditSizeDialog = ({ isOpen, onClose, productSizePriceId }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    sizeOptionId: '',
    price: '',
    description: '',
    image: null,
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  // Fetch size details
  const { data: sizePrice, isLoading } = useQuery(
    ['productSizePrice', productSizePriceId],
    () => getProductSizePrice(productSizePriceId),
    {
      enabled: isOpen && !!productSizePriceId,
    }
  );

  // Fetch size options for dropdown
  const { data: sizeOptionsData } = useQuery(
    'sizeOptions',
    async () => {
      const response = await sizeOptionsAPI.list();
      return response.data.data;
    },
    {
      enabled: isOpen,
    }
  );

  const sizeOptions = sizeOptionsData || [];

  // Fetch image for this size
  const { data: sizeImage } = useQuery(
    ['sizeImage', productSizePriceId],
    async () => {
      const response = await productImagesAPI.getBySizePrice(productSizePriceId);
      return response.data.data;
    },
    {
      enabled: isOpen && !!productSizePriceId,
    }
  );

  // Load size data into form
  useEffect(() => {
    if (sizePrice) {
      setFormData({
        sizeOptionId: sizePrice.size_option_id || '',
        price: sizePrice.price || '',
        description: sizePrice.description || '',
        image: null,
      });
    }
  }, [sizePrice]);

  // Update mutation
  const updateMutation = useMutation(
    async () => {
      const dataToSend = {
        sizeOptionId: formData.sizeOptionId,
        price: parseFloat(formData.price),
        description: formData.description,
      };

      // Update the size price data first
      await updateProductSizePrice(productSizePriceId, dataToSend);

      // If there's a new image file, upload it
      if (formData.image) {
        await productImagesAPI.upload(productSizePriceId, formData.image);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productSizePrice', productSizePriceId]);
        queryClient.invalidateQueries(['sizeImage', productSizePriceId]);
        queryClient.invalidateQueries('products');
        queryClient.invalidateQueries('product');
        queryClient.invalidateQueries('productImages');
        queryClient.invalidateQueries('prices');
        onClose();
      },
      onError: (error) => {
        alert(`Error updating size: ${error.message}`);
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = (file) => {
    setFormData((prev) => ({
      ...prev,
      image: file,
    }));
  };

  const handleImageDelete = async () => {
    if (sizeImage?.url) {
      try {
        setUploadingImage(true);
        await productImagesAPI.delete(productSizePriceId);
        queryClient.invalidateQueries(['sizeImage', productSizePriceId]);
        queryClient.invalidateQueries('productImages');
      } catch (error) {
        alert(`Error deleting image: ${error.message}`);
      } finally {
        setUploadingImage(false);
      }
    }
    setFormData((prev) => ({
      ...prev,
      image: null,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (!formData.sizeOptionId) {
      alert('Size is required');
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      alert('Valid price is required');
      return;
    }

    updateMutation.mutate();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="edit-size-overlay">
      <div className="edit-size-dialog">
        <div className="edit-size-header">
          <h2>Edit Size</h2>
          <button className="close-button" onClick={handleCancel}>
            ×
          </button>
        </div>

        {isLoading ? (
          <div className="edit-size-loading">Loading...</div>
        ) : (
          <form onSubmit={handleSave} className="edit-size-form">
            <div className="form-group">
              <label htmlFor="sizeOptionId">Size *</label>
              <select
                id="sizeOptionId"
                name="sizeOptionId"
                value={formData.sizeOptionId}
                onChange={handleChange}
                required
              >
                <option value="">Select a size</option>
                {sizeOptions.map((size) => (
                  <option key={size.id} value={size.id}>
                    {size.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="price">Price *</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Size-specific details..."
                rows="4"
              />
            </div>

            <div className="form-group">
              <ImageUpload
                label="Product Image"
                currentImageUrl={sizeImage?.url}
                onUpload={handleImageUpload}
                onDelete={handleImageDelete}
                isUploading={uploadingImage}
                maxSize="512KB"
                dimensions="Recommended: 1000x1000px"
                acceptedFormats={['image/jpeg', 'image/png', 'image/webp']}
                maxSizeBytes={524288}
              />
            </div>

            <div className="edit-size-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
                disabled={updateMutation.isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="save-button"
                disabled={updateMutation.isLoading}
              >
                {updateMutation.isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditSizeDialog;
